// React imports
import { useState } from 'react';
import React from 'react';

// MUI imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';

function AdvancedSettingsDialog(props) {
    const { open, onClose, opportunity, onUpdateOpportunity, opportunityId } = props;
    const theme = useTheme();
    const [selectedCategory, setSelectedCategory] = useState('PDF');
    const [viewTemplate, setViewTemplate] = useState(opportunity?.attributes?.viewTemplate || 'standard');

    // Check if all products have quantity 1
    const allProductsQuantityOne = React.useMemo(() => {
        if (!opportunity?.products) return true;
        return opportunity.products.every(product => (product.quantity || 1) === 1);
    }, [opportunity?.products]);

    // Update local state when opportunity changes
    React.useEffect(() => {
        setViewTemplate(opportunity?.attributes?.viewTemplate || 'standard');
    }, [opportunity?.attributes?.viewTemplate, open]);

    function handleSave() {
        // Prevent saving contract template if not all products have quantity 1
        if (viewTemplate === 'contract' && !allProductsQuantityOne) {
            return;
        }
        
        onUpdateOpportunity({ 
            opportunityId, 
            func: opportunity => ({ 
                ...opportunity, 
                attributes: { 
                    ...(opportunity.attributes || {}), 
                    viewTemplate 
                } 
            }) 
        });
        onClose();
    }

    function handleCancel() {
        // Reset to original value
        setViewTemplate(opportunity?.attributes?.viewTemplate || 'standard');
        onClose();
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>Advanced Settings</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', height: '400px' }}>
                    {/* Left side panel - Categories */}
                    <Box sx={{ 
                        width: '200px', 
                        borderRight: `1px solid ${theme.palette.divider}`,
                        paddingRight: '16px'
                    }}>
                        <List>
                            <ListItem disablePadding>
                                <ListItemButton
                                    selected={selectedCategory === 'PDF'}
                                    onClick={() => setSelectedCategory('PDF')}
                                >
                                    <ListItemText primary="PDF & Online view" />
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Box>

                    {/* Right side panel - Settings */}
                    <Box sx={{ flex: 1, paddingLeft: '24px' }}>
                        {selectedCategory === 'PDF' && (
                            <Box>
                                <Typography variant='h6' sx={{ marginBottom: '16px' }}>
                                    PDF Template
                                </Typography>
                                <FormControl fullWidth>
                                    <InputLabel id='pdf-template-label'>PDF Template</InputLabel>
                                    <Select
                                        labelId='pdf-template-label'
                                        id='pdf-template'
                                        label='PDF Template'
                                        value={viewTemplate}
                                        onChange={(e) => setViewTemplate(e.target.value)}
                                    >
                                        <MenuItem value='standard'>
                                            <Box>
                                                <Typography variant='body1'>Standard</Typography>
                                                <Typography variant='caption' sx={{ display: 'block', color: theme.palette.text.secondary }}>
                                                    Monada's standard PDF for opportunities
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value='contract' disabled={!allProductsQuantityOne}>
                                            <Box>
                                                <Typography variant='body1'>Contract</Typography>
                                                <Typography variant='caption' sx={{ display: 'block', color: theme.palette.text.secondary }}>
                                                    PDF view best used when sending long-term contracts.
                                                    {!allProductsQuantityOne && ' (Only available when all products have quantity 1)'}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button 
                    onClick={handleSave} 
                    variant='contained' 
                    color='primary'
                    disabled={viewTemplate === 'contract' && !allProductsQuantityOne}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AdvancedSettingsDialog;

